import {
  UartDataBits,
  UartFlowControl,
  UartOptions,
  UartParity,
  UartStopBits,
  UART_BUFFER_SIZE_MAX,
  UART_BUFFER_SIZE_MIN,
} from '../../define/uart/UartOptions';

/**
 * `UartOptions` 的存取编解码。
 *
 * 用于 `localStorage` —— 那是一份**可能过期、也可能被手改**的外部输入，
 * 所以 `decode` 逐字段校验、坏字段各自回落到默认值，而不是 `JSON.parse as UartOptions` 了事。
 * 后者会让一个坏字段（比如 `baudRate: "abc"`）一路带到 `port.open()` 才炸，
 * 那时的报错（`TypeError: Failed to execute 'open'`）与真正的原因（某次写坏了存储）毫无关系，
 * 排查起来非常费劲。
 *
 * 与仓库其它 codec 同一个形状：`encode` 出字符串、`decode` 进任意值出实体。
 */
export class UartOptionsCodec {
  public static encode(options: UartOptions): string {
    return JSON.stringify(options);
  }

  public static decode(o: unknown): UartOptions {
    const options = new UartOptions();
    if (!isRecord(o)) return options;

    options.baudRate = positiveInt(o['baudRate'], options.baudRate);
    options.dataBits = isDataBits(o['dataBits']) ? o['dataBits'] : options.dataBits;
    options.stopBits = isStopBits(o['stopBits']) ? o['stopBits'] : options.stopBits;
    options.parity = isParity(o['parity']) ? o['parity'] : options.parity;
    options.flowControl = isFlowControl(o['flowControl']) ? o['flowControl'] : options.flowControl;
    options.bufferSize = bufferSize(o['bufferSize'], options.bufferSize);

    return options;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function positiveInt(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : fallback;
}

/*
 * 缓冲区越界时**夹到边界**而不是回落到默认值：越界通常是手工填了个离谱的数，
 * 夹一下还能用；直接重置成 255 反而把「我确实想调大」这个意图丢掉了。
 * 非数字 / 非正数才回落到默认值 —— 那种输入连意图都读不出来。
 */
function bufferSize(value: unknown, fallback: number): number {
  const size = positiveInt(value, fallback);
  return Math.min(Math.max(size, UART_BUFFER_SIZE_MIN), UART_BUFFER_SIZE_MAX);
}

function isDataBits(value: unknown): value is UartDataBits {
  return value === 7 || value === 8;
}

function isStopBits(value: unknown): value is UartStopBits {
  return value === 1 || value === 2;
}

function isParity(value: unknown): value is UartParity {
  return value === 'none' || value === 'even' || value === 'odd';
}

function isFlowControl(value: unknown): value is UartFlowControl {
  return value === 'none' || value === 'hardware';
}
